import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useMutation } from 'react-query';
import type { TripDirection } from 'contracts/transport-routes';
import { useCityOptions, useUfOptions } from 'hooks/api/options';
import { useFetchCityByFieldAndValue } from 'hooks/api/cities';
import { useForwardTripState, useBackwardTripState } from 'pages/Settings/transport-routes/hooks';
import type { SelectContentProps } from 'components/ui/Forms';

import type { FormSchema } from '../form-schema';

type Options = {
  form: UseFormReturn<FormSchema>;
  tripDirection: TripDirection;
}

export const useTripManagerState = ({ tripDirection, form }: Options) => {
  const ufOptions = useUfOptions();
  const { fetchCities } = useCityOptions();
  const { fetchCityByFieldAndValue } = useFetchCityByFieldAndValue();
  const {
    forwardTripFormValues,
    updateForwardTripFormValues,
    updateForwardTripCoordinates,
  } = useForwardTripState();
  const {
    updateBackwardTripFormValues,
    updateBackwardTripCoordinates,
  } = useBackwardTripState();

  const [originCityOptions, setOriginCityOptions]
    = React.useState<SelectContentProps[]>([]);
  const [destinationCityOptions, setDestinationCityOptions]
    = React.useState<SelectContentProps[]>([]);

  const originCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setOriginCityOptions(data.singleSelectOptions);
    },
  });

  const destinationCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setDestinationCityOptions(data.singleSelectOptions);
    },
  });

  const originCityMutation = useMutation({
    mutationFn: fetchCityByFieldAndValue,
  });

  const destinationCityMutation = useMutation({
    mutationFn: fetchCityByFieldAndValue,
  });

  const onOriginUfChange = React.useCallback((uf: string) => {
    form.resetField('origin_ibge');
    originCityOptionsMutation.mutate(uf);

    if (tripDirection === 'forward') {
      updateForwardTripFormValues({
        origin_uf: uf,
        origin_ibge: undefined,
      });
      updateForwardTripCoordinates({
        origin_lat: undefined,
        origin_lng: undefined,
      });
      updateBackwardTripCoordinates({
        destination_lat: undefined,
        destination_lng: undefined,
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripFormValues({
        origin_uf: uf,
        origin_ibge: undefined,
      });
      updateBackwardTripCoordinates({
        origin_lat: undefined,
        origin_lng: undefined,
      });
    }
  }, [
    form,
    originCityOptionsMutation,
    tripDirection,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onOriginIbgeChange = React.useCallback((ibge: string) => {
    if (tripDirection === 'forward') {
      updateForwardTripFormValues({
        origin_ibge: ibge,
      });
      originCityMutation.mutate({ field: 'ibge', value: ibge }, {
        onSuccess: city => {
          updateForwardTripCoordinates({
            origin_lat: city?.latitude,
            origin_lng: city?.longitude,
          });
        },
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripFormValues({
        origin_ibge: ibge,
      });
      originCityMutation.mutate({ field: 'ibge', value: ibge }, {
        onSuccess: city => {
          updateBackwardTripCoordinates({
            origin_lat: city?.latitude,
            origin_lng: city?.longitude,
          });
        },
      });
    }
  }, [
    tripDirection,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onDestinationUfChange = React.useCallback((uf: string) => {
    form.resetField('destination_ibge');
    destinationCityOptionsMutation.mutate(uf);

    if (tripDirection === 'forward') {
      updateForwardTripFormValues({
        destination_uf: uf,
        destination_ibge: undefined,
      });
      updateForwardTripCoordinates({
        destination_lat: undefined,
        destination_lng: undefined,
      });
      updateBackwardTripCoordinates({
        origin_lat: undefined,
        origin_lng: undefined,
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripFormValues({
        destination_uf: uf,
        destination_ibge: undefined,
      });
      updateBackwardTripCoordinates({
        destination_lat: undefined,
        destination_lng: undefined,
      });
    }
  }, [
    form,
    destinationCityOptionsMutation,
    tripDirection,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onDestinationIbgeChange = React.useCallback((ibge: string) => {
    if (tripDirection === 'forward') {
      updateForwardTripFormValues({
        destination_ibge: ibge,
      });
      destinationCityMutation.mutate({ field: 'ibge', value: ibge }, {
        onSuccess: city => {
          updateForwardTripCoordinates({
            destination_lat: city?.latitude,
            destination_lng: city?.longitude,
          });
        },
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripFormValues({
        destination_ibge: ibge,
      });
      destinationCityMutation.mutate({ field: 'ibge', value: ibge }, {
        onSuccess: city => {
          updateBackwardTripCoordinates({
            destination_lat: city?.latitude,
            destination_lng: city?.longitude,
          });
        },
      });
    }
  }, [
    tripDirection,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const triggerUpdateBackwardTripOriginLocation = React.useCallback(() => {
    if (tripDirection !== 'backward') return;

    const { destination_uf, destination_ibge } = forwardTripFormValues;
    if (!destination_uf || !destination_ibge) return;

    originCityOptionsMutation.mutate(destination_uf, {
      onSuccess: () => {
        form.setValue('origin_uf', destination_uf);
        form.setValue('origin_ibge', destination_ibge);
        updateBackwardTripFormValues({
          origin_uf: destination_uf,
          origin_ibge: destination_ibge,
        });
        originCityMutation.mutate({ field: 'ibge', value: destination_ibge }, {
          onSuccess: city => {
            updateBackwardTripCoordinates({
              origin_lat: city?.latitude,
              origin_lng: city?.longitude,
            });
          },
        });
      },
    });
  }, [
    tripDirection,
    form,
    forwardTripFormValues?.destination_ibge,
    updateBackwardTripFormValues,
  ]);

  const triggerUpdateBackwardTripDestinationLocation = React.useCallback(() => {
    if (tripDirection !== 'backward') return;
    const { origin_uf, origin_ibge } = forwardTripFormValues;
    if (!origin_uf || !origin_ibge) return;

    destinationCityOptionsMutation.mutate(origin_uf, {
      onSuccess: () => {
        form.setValue('destination_uf', origin_uf);
        form.setValue('destination_ibge', origin_ibge);
        updateBackwardTripFormValues({
          destination_uf: origin_uf,
          destination_ibge: origin_ibge,
        });
        destinationCityMutation.mutate({ field: 'ibge', value: origin_ibge }, {
          onSuccess: city => {
            updateBackwardTripCoordinates({
              destination_lat: city?.latitude,
              destination_lng: city?.longitude,
            });
          },
        });
      },
    });
  }, [
    tripDirection,
    form,
    forwardTripFormValues?.origin_ibge,
    updateBackwardTripFormValues,
  ]);

  React.useEffect(() => {
    triggerUpdateBackwardTripOriginLocation();
  }, [triggerUpdateBackwardTripOriginLocation]);

  React.useEffect(() => {
    triggerUpdateBackwardTripDestinationLocation();
  }, [triggerUpdateBackwardTripDestinationLocation]);

  return {
    originCityOptions,
    destinationCityOptions,
    ufOptions,
    onOriginUfChange,
    onOriginIbgeChange,
    onDestinationUfChange,
    onDestinationIbgeChange,
  };
};